import React, { useRef, useState } from 'react';
import { FaPrint, FaTimes, FaDownload, FaShare, FaCheck } from 'react-icons/fa';
import html2canvas from 'html2canvas';
import jsPDF from 'jspdf';

export default function AnalyticsScreenModal({ open, onClose, reportContent, chartImages }) {
  const printRef = useRef();
  const [downloading, setDownloading] = useState(false);
  const [copied, setCopied] = useState(false);

  const handlePrint = () => {
    const printWindow = window.open('', '', 'width=1000,height=800');
    printWindow.document.write(`
      <html>
        <head>
          <title>Restaurant Analytics Report</title>
          <style>
            body { font-family: 'Segoe UI', Arial, sans-serif; padding: 20px; background: #f8fafc; color: #22223b; }
            .modal-content { background: #fff; border-radius: 12px; box-shadow: 0 8px 32px rgba(0,0,0,0.12); padding: 24px; max-width: 900px; margin: 0 auto; }
            h1, h2, h3, h4 { font-family: 'Segoe UI', Arial, sans-serif; margin: 0; }
            h1 { font-size: 1.8rem; font-weight: 700; margin-bottom: 8px; }
            h2 { font-size: 1.3rem; font-weight: 600; margin: 20px 0 10px 0; }
            h3 { font-size: 1rem; font-weight: 600; margin: 14px 0 8px 0; }
            .section { margin-bottom: 24px; }
            .section-title { font-size: 1.1rem; font-weight: 600; margin-bottom: 10px; color: #4f46e5; }
            .metrics-table, .data-table { width: 100%; border-collapse: collapse; margin-bottom: 16px; font-size: 14px; }
            .metrics-table th, .metrics-table td, .data-table th, .data-table td { border: 1px solid #e5e7eb; padding: 8px 12px; text-align: left; }
            .metrics-table th, .data-table th { background: #f1f5f9; font-weight: 600; }
            .metrics-table td { font-weight: 500; }
            .metrics-table .change { font-size: 0.9em; color: #059669; font-weight: 400; }
            .date-range { color: #64748b; font-size: 0.9rem; margin-bottom: 16px; }
            .charts-grid { display: flex; flex-wrap: wrap; gap: 16px; margin: 20px 0; }
            .chart-container { flex: 1; min-width: 280px; background: #f8fafc; border-radius: 10px; padding: 14px; box-shadow: 0 2px 8px rgba(0,0,0,0.04); display: flex; flex-direction: column; align-items: center; }
            .chart-title { font-size: 0.9rem; font-weight: 600; margin-bottom: 8px; color: #6366f1; }
            .chart-image { width: 100%; max-width: 300px; height: auto; border-radius: 6px; background: #fff; }
            @media (max-width: 768px) {
              body { padding: 12px; }
              .modal-content { padding: 16px; }
              h1 { font-size: 1.5rem; }
              h2 { font-size: 1.2rem; }
              .metrics-table, .data-table { font-size: 12px; }
              .metrics-table th, .metrics-table td, .data-table th, .data-table td { padding: 6px 8px; }
              .chart-container { min-width: 100%; }
            }
            @media print {
              body { -webkit-print-color-adjust: exact; background: #fff; padding: 0; }
              .modal-content { box-shadow: none; padding: 0; border-radius: 0; }
              .section { page-break-inside: avoid; }
              .chart-container { page-break-inside: avoid; }
            }
          </style>
        </head>
        <body>
          <div class="modal-content">${printRef.current.innerHTML}</div>
        </body>
      </html>
    `);
    printWindow.document.close();
    printWindow.focus();
    setTimeout(() => {
      printWindow.print();
      printWindow.close();
    }, 500);
  };

  const downloadPDF = async () => {
    setDownloading(true);
    try {
      const element = printRef.current;
      const canvas = await html2canvas(element, {
        scale: 2,
        useCORS: true,
        logging: false,
      });
      
      const imgData = canvas.toDataURL('image/png');
      const pdf = new jsPDF('p', 'mm', 'a4');
      const imgProps = pdf.getImageProperties(imgData);
      const pdfWidth = pdf.internal.pageSize.getWidth();
      const pdfHeight = (imgProps.height * pdfWidth) / imgProps.width;
      
      pdf.addImage(imgData, 'PNG', 0, 0, pdfWidth, pdfHeight);
      
      // Generate filename with date
      const dateStr = new Date().toISOString().slice(0, 10);
      pdf.save(`analytics-report-${dateStr}.pdf`);
    } catch (error) {
      console.error('Error generating PDF:', error);
    } finally {
      setDownloading(false);
    }
  };

  const shareReport = async () => {
    if (navigator.share) {
      try {
        // For mobile devices with Web Share API
        await navigator.share({
          title: 'Restaurant Analytics Report',
          text: 'Check out this analytics report for our restaurant',
          url: window.location.href,
        });
      } catch (error) {
        console.log('Sharing cancelled', error);
      }
    } else {
      // Fallback: copy to clipboard
      try {
        const reportText = extractReportText();
        await navigator.clipboard.writeText(reportText);
        setCopied(true);
        setTimeout(() => setCopied(false), 2000);
      } catch (error) {
        console.error('Failed to copy:', error);
        alert('Your browser does not support sharing. Please manually copy the report content.');
      }
    }
  };

  const extractReportText = () => {
    // Extract text content from the report for sharing
    const title = "Restaurant Analytics Report\n";
    const dateRange = printRef.current.querySelector('.date-range')?.textContent || '';
    const metrics = Array.from(printRef.current.querySelectorAll('.metrics-table tr'))
      .map(row => Array.from(row.querySelectorAll('td')).map(cell => cell.textContent).join(': '))
      .join('\n');
    
    return `${title}${dateRange}\n\nKey Metrics:\n${metrics}`;
  };

  if (!open) return null;

  return (
    <div className="fixed inset-0 z-50 bg-black bg-opacity-40 flex items-center justify-center p-4 overflow-auto">
      <div className="bg-white rounded-xl shadow-xl w-full max-w-4xl max-h-[90vh] overflow-y-auto relative p-4 md:p-6" ref={printRef}>
        {/* Close Button */}
        <button
          className="absolute top-3 right-3 text-gray-400 hover:text-gray-700 text-xl p-2 rounded-full bg-gray-100 hover:bg-gray-200"
          onClick={onClose}
          title="Close"
          aria-label="Close"
        >
          <FaTimes />
        </button>
        
        {/* Header with Actions */}
        <div className="flex flex-col sm:flex-row justify-between items-start sm:items-center gap-3 mb-6">
          <h2 className="text-xl md:text-2xl font-bold text-gray-800">Analytics Report</h2>
          <div className="flex flex-wrap gap-2">
            {/* Print Button */}
            <button
              className="bg-indigo-100 text-indigo-700 px-3 py-2 rounded-lg flex items-center gap-1 hover:bg-indigo-200 text-sm"
              onClick={handlePrint}
              aria-label="Print report"
            >
              <FaPrint className="text-sm" /> <span className="hidden sm:inline">Print</span>
            </button>
            
            {/* Download PDF Button */}
            <button
              className="bg-purple-100 text-purple-700 px-3 py-2 rounded-lg flex items-center gap-1 hover:bg-purple-200 text-sm"
              onClick={downloadPDF}
              disabled={downloading}
              aria-label="Download PDF"
            >
              <FaDownload className="text-sm" /> 
              <span className="hidden sm:inline">
                {downloading ? 'Generating...' : 'PDF'}
              </span>
            </button>
            
            {/* Share Button */}
            <button
              className="bg-green-100 text-green-700 px-3 py-2 rounded-lg flex items-center gap-1 hover:bg-green-200 text-sm"
              onClick={shareReport}
              aria-label="Share report"
            >
              {copied ? <FaCheck className="text-sm" /> : <FaShare className="text-sm" />}
              <span className="hidden sm:inline">
                {copied ? 'Copied!' : 'Share'}
              </span>
            </button>
          </div>
        </div>
        
        {/* Report Content */}
        <div className="space-y-5">
          {/* Header and Date Range */}
          <div className="mb-4">
            <h1 className="text-lg md:text-xl font-bold mb-1">Restaurant Analytics Report</h1>
            <div className="date-range text-sm text-gray-500">
              {reportContent.props.children[0].props.children[1]}
            </div>
          </div>
          
          {/* Key Metrics Table */}
          <div className="section">
            <div className="section-title text-base font-semibold text-indigo-600 mb-2">Key Performance Metrics</div>
            <div className="overflow-x-auto">
              <table className="metrics-table w-full text-sm">
                <thead>
                  <tr>
                    <th className="p-2">Metric</th>
                    <th className="p-2">Value</th>
                    <th className="p-2">Change</th>
                  </tr>
                </thead>
                <tbody>
                  <tr>
                    <td className="p-2">Total Revenue</td>
                    <td className="p-2 font-medium">{reportContent.props.children[1].props.children[1].props.children[0].props.children[1].props.children}</td>
                    <td className="p-2 change text-green-600">+12.5% from previous period</td>
                  </tr>
                  <tr>
                    <td className="p-2">Total Orders</td>
                    <td className="p-2 font-medium">{reportContent.props.children[1].props.children[1].props.children[1].props.children[1].props.children}</td>
                    <td className="p-2 change text-green-600">+8.2% from previous period</td>
                  </tr>
                  <tr>
                    <td className="p-2">Average Order Value</td>
                    <td className="p-2 font-medium">{reportContent.props.children[1].props.children[1].props.children[2].props.children[1].props.children}</td>
                    <td className="p-2 change text-green-600">+4.1% from previous period</td>
                  </tr>
                  <tr>
                    <td className="p-2">Unique Customers</td>
                    <td className="p-2 font-medium">{reportContent.props.children[1].props.children[1].props.children[3].props.children[1].props.children}</td>
                    <td className="p-2 change text-green-600">+15.3% from previous period</td>
                  </tr>
                </tbody>
              </table>
            </div>
          </div>
          
          {/* Top Selling Items Table */}
          <div className="section">
            <div className="section-title text-base font-semibold text-indigo-600 mb-2">Top Selling Items</div>
            <div className="overflow-x-auto">
              {reportContent.props.children[2].props.children[1]}
            </div>
          </div>
          
          {/* Daily Revenue Breakdown Table */}
          <div className="section">
            <div className="section-title text-base font-semibold text-indigo-600 mb-2">Daily Revenue Breakdown</div>
            <div className="overflow-x-auto">
              {reportContent.props.children[3].props.children[1]}
            </div>
          </div>
          
          {/* Order Status Summary Table */}
          <div className="section">
            <div className="section-title text-base font-semibold text-indigo-600 mb-2">Order Status Summary</div>
            <div className="overflow-x-auto">
              {reportContent.props.children[4].props.children[1]}
            </div>
          </div>
          
          {/* Recent Orders Table */}
          <div className="section">
            <div className="section-title text-base font-semibold text-indigo-600 mb-2">Recent Orders</div>
            <div className="overflow-x-auto">
              {reportContent.props.children[5].props.children[1]}
            </div>
          </div>
        </div>
        
        {/* Charts Section */}
        {chartImages && (
          <div className="mt-6">
            <div className="section-title text-base font-semibold text-indigo-600 mb-3">Charts & Visualizations</div>
            <div className="charts-grid grid grid-cols-1 md:grid-cols-2 gap-4">
              {chartImages.revenue && (
                <div className="chart-container bg-gray-50 p-4 rounded-lg">
                  <div className="chart-title text-sm font-semibold text-center mb-2">Revenue Trend Analysis</div>
                  <img src={chartImages.revenue} alt="Revenue Trend" className="chart-image mx-auto" />
                </div>
              )}
              {chartImages.topItems && (
                <div className="chart-container bg-gray-50 p-4 rounded-lg">
                  <div className="chart-title text-sm font-semibold text-center mb-2">Top Selling Items Distribution</div>
                  <img src={chartImages.topItems} alt="Top Selling Items" className="chart-image mx-auto" />
                </div>
              )}
              {chartImages.orderVolume && (
                <div className="chart-container bg-gray-50 p-4 rounded-lg md:col-span-2">
                  <div className="chart-title text-sm font-semibold text-center mb-2">Daily Order Volume</div>
                  <img src={chartImages.orderVolume} alt="Order Volume" className="chart-image mx-auto max-w-md" />
                </div>
              )}
            </div>
          </div>
        )}
        
        
      </div>
    </div>
  );
}